import React from "react";
import { Box, Checkbox, FormControlLabel, Typography } from "@mui/material";

const sectionTitleSx = { mt: 4, mb: 1, fontWeight: 700 };
const labelSx = { width: "180px", fontWeight: 700, flexShrink: 0 };
const valueSx = {
  flex: 1,
  borderBottom: "1px solid #999",
  minHeight: "20px",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
};
const rowSx = { display: "flex", gap: 12, mb: 1.2, alignItems: "flex-start" };
const gridSx = {
  display: "grid",
  gridTemplateColumns: "180px 1fr 180px 1fr",
  gap: "6px 12px",
  mb: 2,
};

const valueOrEmpty = (value) => value ?? "";
const boolLabel = (value, yes = "あり", no = "なし") => {
  if (value === true) return yes;
  if (value === false) return no;
  return "";
};
const sameAsLabel = (value) => {
  if (value === "same") return "同じ";
  if (value === "different") return "異なる";
  return "";
};

const LabeledRow = ({ label, value }) => (
  <Box sx={rowSx}>
    <Typography sx={labelSx}>{label}</Typography>
    <Box sx={valueSx}>{valueOrEmpty(value)}</Box>
  </Box>
);

const ContactGrid = ({ rows }) => (
  <Box sx={gridSx}>
    {rows.map(([label, value]) => (
      <React.Fragment key={label}>
        <Box sx={{ fontWeight: 700 }}>{label}</Box>
        <Box sx={valueSx}>{valueOrEmpty(value)}</Box>
      </React.Fragment>
    ))}
  </Box>
);

const DetailList = ({ title, rows, type }) => (
  <Box sx={{ mt: 3 }}>
    <Typography sx={sectionTitleSx}>{title}</Typography>
    {!rows?.length && <Typography>入力なし</Typography>}
    {rows?.map((row, index) => (
      <Box key={index} sx={{ mb: 2, pl: 1 }}>
        <Typography sx={{ mb: 1, fontWeight: 700 }}>{`${type} ${index + 1}`}</Typography>
        <ContactGrid
          rows={
            type === "100V"
              ? [
                  ["機器名", row.device_100_name],
                  ["容量(W)", row.quantity_100],
                  ["数量", row.number_100],
                  ["合計", row.total_100],
                  ["備考", row.ex_100],
                ]
              : [
                  ["機器名", row.device_200_name],
                  ["容量(W)", row.quantity_200],
                  ["数量", row.number_200],
                  ["合計", row.total_200],
                  ["備考", row.ex_200],
                ]
          }
        />
      </Box>
    ))}
  </Box>
);

const Comp5PDF = ({ elecData }) => {
  if (!elecData) return null;

  const formData = elecData?.o5?.[0] ?? elecData;
  const details100 = Array.isArray(formData.details_100) ? formData.details_100 : [];
  const details200 = Array.isArray(formData.details_200) ? formData.details_200 : [];
  const uses100 = details100.length > 0;
  const uses200 = details200.length > 0;
  const isNoApply =
    formData?.exhibitor_status === "申請しない" ||
    (formData?.host_status === "申請しない" && formData?.corporate_status === "申請しない");

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 3 }}>
        電気使用申込
      </Typography>

      <LabeledRow label="小間番号" value={elecData.booth_number} />
      <LabeledRow label="会社名" value={elecData.company_name} />
      <LabeledRow label="部署" value={elecData.department} />
      <LabeledRow label="担当者名" value={elecData.user_name} />
      <LabeledRow label="郵便番号" value={elecData.zip_code} />
      <LabeledRow label="住所" value={elecData.address} />
      <LabeledRow label="電話番号" value={elecData.phone_number} />
      <LabeledRow label="メールアドレス" value={elecData.email} />

      <FormControlLabel control={<Checkbox checked={!!isNoApply} readOnly />} label="申請しない" />

      {!isNoApply && (
        <>
          <Typography sx={sectionTitleSx}>担当者情報</Typography>
          <LabeledRow label="出展者情報と同じ" value={sameAsLabel(formData.same_ex_5)} />
          <ContactGrid
            rows={[
              ["担当者名", formData.user_5_name],
              ["部署", formData.user_5_department],
              ["会社電話番号", formData.user_5_phone],
              ["携帯電話番号", formData.user_5_tel],
            ]}
          />

          <Typography sx={sectionTitleSx}>請求先情報</Typography>
          <LabeledRow label="出展者情報と同じ" value={sameAsLabel(formData.same_in_5)} />
          <ContactGrid
            rows={[
              ["会社名", formData.invoice_company_name],
              ["担当者名", formData.invoice_user_name],
              ["メールアドレス", formData.invoice_email],
              ["郵便番号", formData.invoice_zip],
              ["住所", formData.invoice_address],
              ["電話番号", formData.invoice_tel],
              ["FAX番号", formData.invoice_fax],
            ]}
          />

          <Typography sx={sectionTitleSx}>小間電気工事会社</Typography>
          <LabeledRow label="主催者指定業者へ依頼" value={boolLabel(formData.elec_select, "依頼する", "依頼しない")} />
          {!formData.elec_select && (
            <ContactGrid
              rows={[
                ["会社名", formData.elec_company_name],
                ["郵便番号", formData.elec_zip],
                ["住所", formData.elec_address],
                ["担当者名", formData.elec_user_name],
                ["電話番号", formData.elec_tel],
                ["FAX番号", formData.elec_fax],
              ]}
            />
          )}

          <Typography sx={sectionTitleSx}>基本供給電力</Typography>
          <LabeledRow label="基本供給電力を使用する" value={boolLabel(formData.ordi_elec, "使用する", "使用しない")} />

          {formData.ordi_elec && (
            <>
              <Typography sx={sectionTitleSx}>追加電気設備</Typography>
              <FormControlLabel control={<Checkbox checked={uses100} readOnly />} label="100Vを使用する" />
              <FormControlLabel control={<Checkbox checked={uses200} readOnly />} label="200Vを使用する" />
              <FormControlLabel control={<Checkbox checked={!!formData.quantity_100v} readOnly />} label="100V 終夜送電あり" />
              <FormControlLabel control={<Checkbox checked={!!formData.quantity_200v} readOnly />} label="200V 終夜送電あり" />
              <LabeledRow label="合計容量(W)" value={formData.quantity_all} />

              {uses100 && <DetailList title="100V 使用詳細" rows={details100} type="100V" />}
              {uses200 && <DetailList title="200V 使用詳細" rows={details200} type="200V" />}
            </>
          )}

          <Typography sx={sectionTitleSx}>備考</Typography>
          <Box sx={valueSx}>{valueOrEmpty(formData.alfa_text)}</Box>
        </>
      )}
    </Box>
  );
};

export default Comp5PDF;
